import React, { useCallback, useState, createContext, useContext } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';
export type ToastType = 'success' | 'error' | 'info';
interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}
interface ToastContextType {
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}
const ToastContext = createContext<ToastContextType | undefined>(undefined);
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
export function ToastProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts(prev => [...prev, {
      ...toast,
      id
    }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 5000);
  }, []);
  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);
  return <ToastContext.Provider value={{
    addToast,
    removeToast
  }} data-id="element-1250">
      {children}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 w-full max-w-sm pointer-events-none" data-id="element-1251">
        <AnimatePresence data-id="element-1252">
          {toasts.map(toast => <ToastItem key={toast.id} toast={toast} onDismiss={removeToast} data-id="element-1253" />)}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>;
}
function ToastItem({
  toast,
  onDismiss
}: {
  toast: Toast;
  onDismiss: (id: string) => void;
}) {
  const icons = {
    success: <CheckCircle className="h-5 w-5 text-green-500" data-id="element-1254" />,
    error: <AlertCircle className="h-5 w-5 text-red-500" data-id="element-1255" />,
    info: <Info className="h-5 w-5 text-orange-500" data-id="element-1256" />
  };
  const borderColors = {
    success: 'border-l-green-500',
    error: 'border-l-red-500',
    info: 'border-l-orange-500'
  };
  return <motion.div initial={{
    opacity: 0,
    x: 50,
    scale: 0.95
  }} animate={{
    opacity: 1,
    x: 0,
    scale: 1
  }} exit={{
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.2
    }
  }} className={`pointer-events-auto bg-white rounded-xl shadow-xl border border-gray-100 border-l-4 ${borderColors[toast.type]} p-4 flex items-start gap-3`} role="alert" data-id="element-1257">
      <div className="flex-shrink-0 mt-0.5" data-id="element-1258">{icons[toast.type]}</div>
      <div className="flex-1 min-w-0" data-id="element-1259">
        <h4 className="text-sm font-medium text-gray-900" data-id="element-1260">{toast.title}</h4>
        {toast.message && <p className="mt-1 text-sm text-gray-500" data-id="element-1261">{toast.message}</p>}
        {toast.action && <button onClick={toast.action.onClick} className="mt-2 text-sm font-medium text-orange-600 hover:text-orange-500" data-id="element-1262">
            {toast.action.label}
          </button>}
      </div>
      <button onClick={() => onDismiss(toast.id)} className="flex-shrink-0 text-gray-400 hover:text-gray-500 transition-colors" aria-label="Close notification" data-id="element-1263">
        <X className="h-4 w-4" data-id="element-1264" />
      </button>
    </motion.div>;
}